import {
  Button,
  Group,
  MantineSize,
  Modal,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { SvgIconTypeMap } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { useState } from "react";
import { setNotification } from "../notifications";
import classes from "./CustomModal.module.css";

interface Props {
  style?: "default" | "warning";
  size?: MantineSize | string | number;
  title: string;
  TitleIcon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
    muiName: string;
  };
  opened: boolean;
  onClose?: () => void;
  onSubmit?: () => Promise<void>;
  children: React.ReactNode;
  ctaLabel?: string;
  ctaDisabled?: boolean;
  ctaLoading?: boolean;
}

export default function CustomModal({
  style = "default",
  size = "md",
  title,
  TitleIcon,
  opened,
  onClose,
  onSubmit,
  children,
  ctaLabel,
  ctaDisabled,
  ctaLoading,
}: Props) {
  const theme = useMantineTheme();
  const scheme = useComputedColorScheme("light");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const handleSubmit = async () => {
    if (!onSubmit) return;
    setError(false);
    setLoading(true);
    try {
      await onSubmit();
      onClose?.();
    } catch (err: Error | unknown) {
      const error = err as Error;
      setError(true);
      setNotification("Error", false, error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const modalAccent = getModalPrimaryColor(error, style);
  const titleIconTint =
    scheme === "dark" ? theme.colors.gray[1] : theme.colors[modalAccent][6];

  return (
    <Modal
      radius="md"
      opened={opened}
      onClose={onClose ?? (() => {})}
      size={size}
      withCloseButton
      title={
        <Group>
          <TitleIcon
            style={{ width: 28, height: 28 }}
            htmlColor={titleIconTint}
          />
          <Text fw={700} c={scheme === "dark" ? "gray.1" : "darkblue"}>
            {title}
          </Text>
        </Group>
      }
      classNames={{
        body: classes.modalBody,
        header: classes.modalHeader,
        content: classes.modalContent,
      }}
    >
      {children}
      {onSubmit && (
        <Button
          color={modalAccent}
          fullWidth
          size="md"
          loading={loading || ctaLoading}
          onClick={handleSubmit}
          className={classes.submitButton}
          disabled={ctaDisabled}
        >
          {error
            ? "Try Again"
            : loading || ctaLoading
            ? "Loading..."
            : ctaLabel}
        </Button>
      )}
    </Modal>
  );
}

function getModalPrimaryColor(error: boolean, style: "default" | "warning") {
  if (error) return "incorrect";
  if (style === "warning") return "incorrect";
  return "darkblue";
}
